import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Flame, Star, Users, Calendar, BookOpen, Bell, Zap, Trophy } from 'lucide-react';

const BACKEND_URL = '';

const CATEGORY_ICONS = {
  profile: Star,
  networking: Users,
  events: Calendar,
  learning: BookOpen,
  activity: Bell,
  proactiveness: Zap,
};

const CATEGORY_COLORS = {
  profile: { bar: 'bg-amber-400', bg: 'bg-amber-50', text: 'text-amber-700', ring: 'ring-amber-200' },
  networking: { bar: 'bg-brand-navy/50', bg: 'bg-brand-navy/5', text: 'text-brand-navy', ring: 'ring-brand-blue/40' },
  events: { bar: 'bg-violet-500', bg: 'bg-violet-50', text: 'text-violet-700', ring: 'ring-violet-200' },
  learning: { bar: 'bg-brand-sage', bg: 'bg-brand-sage/10', text: 'text-brand-sage', ring: 'ring-brand-sage/30' },
  activity: { bar: 'bg-rose-400', bg: 'bg-rose-50', text: 'text-rose-700', ring: 'ring-rose-200' },
  proactiveness: { bar: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-200' },
};

export default function XPProfileCard() {
  const [xpData, setXpData] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('session_token');
    fetch(`${BACKEND_URL}/api/members/xp`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(setXpData)
      .catch(() => {});
  }, []);

  if (!xpData) return null;

  const { total_xp, breakdown, level, next_level, progress_to_next } = xpData;
  const maxCategoryXP = Math.max(...Object.values(breakdown).map(v => v.xp), 1);

  return (
    <Card data-testid="xp-profile-card" className="p-0 overflow-hidden border border-slate-200">
      {/* Header with level & total XP */}
      <div className="relative bg-gradient-to-r from-brand-dark via-brand-dark to-brand-navy p-6 pb-8">
        <div className="absolute inset-0 opacity-10"
          style={{backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(6,182,212,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(16,185,129,0.3) 0%, transparent 50%)'}} />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="h-5 w-5 text-amber-400" />
              <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">Level {level.level}</span>
            </div>
            <h3 className="text-2xl font-bold text-white font-heading">{level.name}</h3>
          </div>
          <div className="text-right">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-white">{total_xp.toLocaleString()}</span>
              <span className="text-brand-blue/60 text-sm font-medium">XP</span>
            </div>
          </div>
        </div>

        {/* Progress bar to next level */}
        {next_level && (
          <div className="relative mt-5">
            <div className="flex justify-between text-xs text-slate-400 mb-1.5">
              <span>Lvl {level.level}</span>
              <span>{Math.round(progress_to_next)}%</span>
              <span>Lvl {next_level.level} — {next_level.name}</span>
            </div>
            <div className="h-2.5 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-brand-blue to-brand-green transition-all duration-1000"
                style={{ width: `${Math.min(progress_to_next, 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              {next_level.min_xp - total_xp} XP to {next_level.name}
            </p>
          </div>
        )}
        {!next_level && (
          <p className="text-brand-green text-sm mt-3 flex items-center gap-1.5">
            <Flame className="h-4 w-4" /> Max level reached!
          </p>
        )}
      </div>

      {/* XP Breakdown */}
      <div className="p-6">
        <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">XP Breakdown</h4>
        <div className="space-y-4">
          {Object.entries(breakdown).map(([key, val]) => {
            const Icon = CATEGORY_ICONS[key] || Star;
            const colors = CATEGORY_COLORS[key] || CATEGORY_COLORS.profile;
            const pct = Math.min((val.xp / maxCategoryXP) * 100, 100);
            return (
              <div key={key} data-testid={`xp-category-${key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center ring-1 ${colors.ring}`}>
                      <Icon className={`h-4 w-4 ${colors.text}`} />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-700">{val.label}</span>
                      <p className="text-xs text-slate-400">{val.detail}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-slate-800 tabular-nums">{val.xp} XP</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden ml-[42px]">
                  <div
                    className={`h-full rounded-full ${colors.bar} transition-all duration-700`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tip */}
      <div className="px-6 pb-5">
        <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
          <p className="text-xs text-slate-500">
            <strong className="text-slate-700">Earn more XP:</strong> Complete your profile, attend events, enrol in courses, and connect with other members.
          </p>
        </div>
      </div>
    </Card>
  );
}
